import { useCallback, useEffect, useRef, useState } from 'react';
import ChestnyZnakLabeler from '../../../free-tools/ChestnyZnakLabeler';
import OrderTape from '../../../free-tools/OrderTape';
import { LABEL_SIZES } from '../../../free-tools/chestnyZnakPrint';
import { useApi } from '../../../../hooks/useApi';
import logger from '../../../../utils/logger';
import '../../../free-tools/ChestnyZnakLabeler.css';
import usePersistedState from '../../../../hooks/usePersistedState';

const MODES = [
    { id: 'print', label: 'Печать этикетки' },
    { id: 'tape', label: 'Лента заказов' },
];

const DEFAULT_LABEL_SIZE = LABEL_SIZES.find((s) => s.id === '58x40');

const DEFAULT_SETTINGS = {
    font: 'Arial',
    labelSize: DEFAULT_LABEL_SIZE,
    rotated: false,
    copies: 1,
    autoPrint: true,
    titleEnabled: false,
    dateFields: { mfg: false, shelf: false, exp: false },
    barcodeEnabled: false,
    barcodeFormat: 'CODE128',
};

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 350;

function OzonFbsChzPrint({ accountId }) {
    const { request } = useApi();
    // Подвкладка переживает F5, как и в FBS.
    const [mode, setMode] = usePersistedState(`ozon-chz-mode-${accountId || 'none'}`, 'print');
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [query, setQuery] = useState('');
    const [candidates, setCandidates] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [prefill, setPrefill] = useState(null);
    const nonceRef = useRef(0);
    const requestSeqRef = useRef(0);

    const updateSettings = useCallback((patch) => {
        setSettings((prev) => ({ ...prev, ...patch }));
    }, []);

    const applyCandidate = useCallback((item) => {
        nonceRef.current += 1;
        setPrefill({ title: item.name || '', barcodeValue: item.barcode || '', nonce: nonceRef.current });
        setCandidates([]);
        setQuery('');
    }, []);

    const runSearch = useCallback(async (q) => {
        const seq = ++requestSeqRef.current;
        setSearching(true);
        setSearchError('');
        try {
            const data = await request(`/api/v1/ozon/${accountId}/attributes/lookup?query=${encodeURIComponent(q)}`);
            if (seq !== requestSeqRef.current) return; // stale response — a newer query has since started
            const items = Array.isArray(data) ? data : [];
            setCandidates(items);
            if (items.length === 0) {
                setSearchError('Товар не найден в базе «Атрибуты». Проверьте синхронизацию атрибутов.');
            }
        } catch (err) {
            if (seq !== requestSeqRef.current) return;
            logger.error(err);
            setCandidates([]);
            setSearchError(err?.message || 'Ошибка поиска товара.');
        } finally {
            if (seq === requestSeqRef.current) setSearching(false);
        }
    }, [accountId, request]);

    // Live search-as-you-type: debounce the request instead of requiring a submit click.
    useEffect(() => {
        const q = query.trim();
        if (q.length < MIN_QUERY_LENGTH) {
            requestSeqRef.current += 1; // invalidate any in-flight request
            setCandidates([]);
            setSearching(false);
            setSearchError('');
            return;
        }
        const timer = setTimeout(() => runSearch(q), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query, runSearch]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const q = query.trim();
        if (q.length >= MIN_QUERY_LENGTH) runSearch(q);
    };

    // Для «Ленты заказов» — та же база «Атрибуты», debounce уже реализован внутри OrderTape.
    const attributeSearch = useCallback(async (q) => {
        const data = await request(`/api/v1/ozon/${accountId}/attributes/lookup?query=${encodeURIComponent(q)}`);
        return Array.isArray(data) ? data : [];
    }, [accountId, request]);

    return (
        <div className="stack-12">
            <div className="cz-tool__tabs" role="tablist" aria-label="Режим работы с Честным знаком">
                {MODES.map((m) => (
                    <button
                        key={m.id}
                        type="button"
                        role="tab"
                        aria-selected={mode === m.id}
                        className={`cz-tool__tab${mode === m.id ? ' cz-tool__tab--active' : ''}`}
                        onClick={() => setMode(m.id)}
                    >
                        {m.label}
                    </button>
                ))}
            </div>

            {mode === 'print' && (
                <form className="stack-8" onSubmit={handleSubmit}>
                    <label className="bl__label">Найти товар (артикул, SKU, штрихкод или название)</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input
                            className="bl__input"
                            style={{ flex: 1 }}
                            type="text"
                            placeholder="Артикул, SKU, штрихкод или название товара..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            autoComplete="off"
                        />
                    </div>
                    {searching && <p className="muted" style={{ margin: 0, fontSize: 12 }}>Поиск…</p>}
                    {!searching && searchError && <div className="cz__scan-warning">{searchError}</div>}
                    {!searching && candidates.length > 0 && (
                        <div className="analytics-table-wrapper">
                            <table>
                                <thead><tr><th>Артикул</th><th>SKU</th><th>Название</th><th>Штрихкод</th><th /></tr></thead>
                                <tbody>
                                    {candidates.map((c) => (
                                        <tr key={`${c.offer_id}-${c.sku}`}>
                                            <td>{c.offer_id || '—'}</td>
                                            <td>{c.sku || '—'}</td>
                                            <td>{c.name || '—'}</td>
                                            <td>{c.barcode || '—'}</td>
                                            <td><button type="button" onClick={() => applyCandidate(c)}>Выбрать</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </form>
            )}

            {mode === 'print' && <ChestnyZnakLabeler settings={settings} onSettingsChange={updateSettings} prefill={prefill} />}
            {mode === 'tape' && <OrderTape settings={settings} onSettingsChange={updateSettings} attributeSearch={attributeSearch} />}
        </div>
    );
}

export default OzonFbsChzPrint;
